/* Optimizer v3.1 - shares fix - 1773303204.3619382 */
/**
 * BESVOL Firebase Social - 极速加载版 V3.1
 * 修复：移除重复方法声明
 */
class BesvolSocialOptimizer {
  constructor(config = {}) {
    this.config = {
      cacheKey: 'besvol_social_v3',
      cacheTTL: 300000,
      staleTTL: 86400000,
      batchSize: 50,
      preloadOffset: 200,
      ...config
    };
    
    this.memoryCache = new Map();
    this.localCache = null;
    this.updateQueue = [];
    this.observers = new Map();
    this.isOnline = navigator.onLine;
    this.metrics = { cacheHits: 0, firebaseRequests: 0, renderTime: 0 };
    
    this.init();
  }

  async init() {
    const startTime = performance.now();

    // 1. 立即从LocalStorage加载（快速显示缓存）
    this.localCache = this.loadFromLocalStorage();
    if (this.localCache) {
      this.hydrateMemoryCache(this.localCache);
      this.renderVisibleCards();
      this.metrics.cacheHits++;
      console.log('[BesvolSocial] ✅ Cache hit - instant render');
    }

    // 2. 后台连接Firebase（不阻塞，数据更新时自动刷新）
    if (this.isOnline) {
      this.connectFirebase();
    }

    // 3. 设置懒加载
    this.setupLazyLoading();
    this.setupNetworkListener();
    this.startSyncLoop();

    this.metrics.initTime = performance.now() - startTime;
    console.log(`[BesvolSocial] ⚡ Initialized in ${this.metrics.initTime.toFixed(2)}ms`);
    console.log(`[BesvolSocial] 📊 Cache status: ${this.memoryCache.size} items`);
  }

  loadFromLocalStorage() {
    try {
      const raw = localStorage.getItem(this.config.cacheKey);
      if (!raw) return null;
      
      const data = JSON.parse(raw);
      const age = Date.now() - data.timestamp;
      
      if (age > this.config.staleTTL) {
        localStorage.removeItem(this.config.cacheKey);
        return null;
      }
      
      return { ...data, _isStale: age > this.config.cacheTTL };
    } catch (e) {
      return null;
    }
  }

  saveToLocalStorage() {
    try {
      const data = {
        timestamp: Date.now(),
        products: Object.fromEntries(this.memoryCache),
        version: 'v3'
      };
      localStorage.setItem(this.config.cacheKey, JSON.stringify(data));
    } catch (e) {}
  }

  hydrateMemoryCache(data) {
    if (!data.products) return;
    Object.entries(data.products).forEach(([id, metrics]) => {
      this.memoryCache.set(id, { ...metrics, _lastUpdate: data.timestamp });
    });
  }

  connectFirebase() {
    console.log('[BesvolSocial] connectFirebase called, besvolDB:', !!window.besvolDB);

    if (!window.besvolDB) {
      console.log('[BesvolSocial] ⏳ Firebase not ready, will retry...');
      setTimeout(() => this.connectFirebase(), 500);
      return;
    }

    console.log('[BesvolSocial] ✅ Firebase connected');
    const visibleIds = this.getVisibleIds();
    console.log(`[BesvolSocial] 👁️ Visible items: ${visibleIds.length}`);
    
    this.fetchBatchData(visibleIds);
    this.setupRealtimeListeners(visibleIds);
  }

  getVisibleIds() {
    const ids = [];
    const selectors = ['[data-product-id]', '[data-article-id]'];
    
    selectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => {
        const id = el.dataset.productId || el.dataset.articleId;
        if (id) {
          const rect = el.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight + this.config.preloadOffset 
                           && rect.bottom > -this.config.preloadOffset;
          if (isVisible && !ids.includes(id)) {
            ids.push(id);
          }
        }
      });
    });
    
    return ids;
  }

  async fetchBatchData(ids) {
    if (!ids.length || !window.besvolDB) return;
    
    const db = window.besvolDB;
    const startTime = performance.now();
    
    const promises = ids.map(id => 
      db.ref(`articles/${id}`).once('value')
        .then(snap => ({ id, data: snap.val() }))
        .catch(() => null)
    );
    
    const results = await Promise.all(promises);
    this.metrics.firebaseRequests += ids.length;
    
    let hasUpdate = false;
    results.forEach(result => {
      if (result?.data) {
        const cached = this.memoryCache.get(result.id);
        if (!cached || JSON.stringify(cached) !== JSON.stringify(result.data)) {
          this.memoryCache.set(result.id, { ...result.data, _lastUpdate: Date.now() });
          hasUpdate = true;
        }
      }
    });
    
    console.log(`[BesvolSocial] 📥 Fetched ${ids.length} items in ${(performance.now() - startTime).toFixed(2)}ms`);
    
    if (hasUpdate) {
      this.batchRender();
      this.saveToLocalStorage();
    }
  }

  setupRealtimeListeners(ids) {
    if (!window.besvolDB) return;
    
    ids.forEach(id => {
      if (this.observers.has(id)) return;
      
      const ref = window.besvolDB.ref(`articles/${id}`);
      const listener = ref.on('value', snap => {
        const data = snap.val();
        if (data) {
          this.memoryCache.set(id, { ...data, _lastUpdate: Date.now() });
          this.queueUpdate(id, data);
        }
      });
      
      this.observers.set(id, () => ref.off('value', listener));
    });
    
    console.log(`[BesvolSocial] 👂 Listening to ${this.observers.size} items`);
  }

  setupLazyLoading() {
    if (!('IntersectionObserver' in window)) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const el = entry.target;
          const id = el.dataset.productId || el.dataset.articleId;
          
          if (id && !this.memoryCache.has(id) && this.isOnline) {
            this.fetchBatchData([id]);
          } else {
            this.renderItem(id);
          }
          
          if (this.memoryCache.has(id)) {
            observer.unobserve(el);
          }
        }
      });
    }, { rootMargin: `${this.config.preloadOffset}px`, threshold: 0.1 });
    
    document.querySelectorAll('[data-product-id], [data-article-id]').forEach(el => {
      observer.observe(el);
    });
  }

  queueUpdate(id, data) {
    this.updateQueue.push({ id, data });
    if (this.updateTimer) cancelAnimationFrame(this.updateTimer);
    this.updateTimer = requestAnimationFrame(() => this.flushUpdates());
  }

  flushUpdates() {
    if (!this.updateQueue.length) return;
    
    const updates = [...this.updateQueue];
    this.updateQueue = [];
    
    const startTime = performance.now();
    updates.forEach(({ id }) => this.renderItem(id));
    this.metrics.renderTime += performance.now() - startTime;
  }

  renderItem(id) {
    const metrics = this.memoryCache.get(id);
    if (!metrics) return;
    
    // 查找元素（支持product-id和article-id）
    const container = document.querySelector(`[data-product-id="${id}"]`) 
                   || document.querySelector(`[data-article-id="${id}"]`);
    if (!container) return;
    
    const elements = {
      likes: container.querySelector('[data-social="likes"]'),
      shares: container.querySelector('[data-social="shares"]'),
      views: container.querySelector('[data-social="views"]'),
      votes: container.querySelector('[data-social="votes"]')
    };
    
    Object.entries(elements).forEach(([type, el]) => {
      if (!el) return;
      const newValue = this.formatNumber(metrics[type] || 0);
      if (el.textContent !== newValue) {
        el.textContent = newValue;
        el.classList.add('social-update');
        setTimeout(() => el.classList.remove('social-update'), 300);
      }
    });
  }

  renderVisibleCards() {
    const visibleIds = this.getVisibleIds();
    visibleIds.forEach(id => this.renderItem(id));
  }

  batchRender() {
    requestAnimationFrame(() => this.renderVisibleCards());
  }

  formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  }

  setupNetworkListener() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.connectFirebase();
      this.syncPending();
    });
    window.addEventListener('offline', () => this.isOnline = false);
  }

  startSyncLoop() {
    setInterval(() => { if (this.isOnline) this.syncPending(); }, 30000);
  }

  async interact(id, action) {
    const actionKey = `besvol_${id}_${action}`;
    if (localStorage.getItem(actionKey)) return false;
    
    const current = this.memoryCache.get(id) || {};
    const newValue = (current[action] || 0) + 1;
    
    this.memoryCache.set(id, { ...current, [action]: newValue });
    this.renderItem(id);
    this.saveToLocalStorage();
    localStorage.setItem(actionKey, Date.now().toString());
    
    if (this.isOnline && window.besvolDB) {
      try {
        await window.besvolDB.ref(`articles/${id}/${action}`).set(newValue);
      } catch (e) {
        this.queueForSync(id, action, newValue);
      }
    } else {
      this.queueForSync(id, action, newValue);
    }
    
    return true;
  }

  queueForSync(id, action, value) {
    const pending = JSON.parse(localStorage.getItem('besvol_pending') || '[]');
    pending.push({ id, action, value, timestamp: Date.now() });
    localStorage.setItem('besvol_pending', JSON.stringify(pending));
  }

  async syncPending() {
    const pending = JSON.parse(localStorage.getItem('besvol_pending') || '[]');
    if (!pending.length || !window.besvolDB) return;
    
    const results = await Promise.all(
      pending.map(item => 
        window.besvolDB.ref(`articles/${item.id}/${item.action}`).set(item.value)
          .then(() => ({ success: true }))
          .catch(() => ({ success: false, item }))
      )
    );
    
    const failed = results.filter(r => !r.success).map(r => r.item);
    localStorage.setItem('besvol_pending', JSON.stringify(failed));
  }

  getMetrics() {
    return {
      ...this.metrics,
      cacheSize: this.memoryCache.size,
      observers: this.observers.size,
      isOnline: this.isOnline,
      timestamp: Date.now()
    };
  }

  destroy() {
    this.observers.forEach(off => off());
    this.observers.clear();
    this.memoryCache.clear();
  }
}

// 初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.BESVOL = window.BESVOL || {};
    window.BESVOL.social = new BesvolSocialOptimizer();
  });
} else {
  window.BESVOL = window.BESVOL || {};
  window.BESVOL.social = new BesvolSocialOptimizer();
}
